import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/RatePlansService';
import { RatePlan } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useRatePlan = (ratePlanId?: string) => {
    const queryClient = useQueryClient();

    const ratePlanQuery = useQuery({
        queryKey: ['rate-plan', ratePlanId],
        queryFn: () => fetch(ratePlanId!),
        enabled: !!ratePlanId
    });

    const createMutation = useMutation({
        mutationFn: (values: RatePlan) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            notifySuccess('Rate Plan created');
            queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: RatePlan) => {
            if (!ratePlanId) throw new Error('ratePlanId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(ratePlanId, rest);
        },
        onSuccess: () => {
            notifySuccess('Rate Plan updated');
            queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
            queryClient.invalidateQueries({ queryKey: ['rate-plan', ratePlanId] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!ratePlanId) throw new Error('ratePlanId is required parameter');
            return deleteRecord(ratePlanId);
        },
        onSuccess: () => {
            notifySuccess('Rate Plan deleted');
            queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    return {
        record: ratePlanQuery.data,
        loading: ratePlanQuery.isLoading,
        fetchRecord: ratePlanQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};